import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Insight } from 'libs/end-to-end/src/insights';
import { LoadingSkeleton } from 'apps/sigma2-ui/src/components/LoadingSkeleton';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { getInsightKey } from 'apps/sigma2-ui/src/pages/InsightsSection.utils';
import { InsightGroupProps } from 'apps/sigma2-ui/src/pages/InsightsSection.utils';
import { groupInsights } from 'apps/sigma2-ui/src/pages/InsightsSection.utils';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';
import { FilterBar } from 'apps/sigma2-ui/src/components/FilterBar';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { InsightsCard } from 'apps/sigma2-ui/src/components/insights/InsightsCard';
import { DimensionSelect } from 'apps/sigma2-ui/src/lib/hooks/useDynamicDimensions';
import { useDynamicDimensions } from 'apps/sigma2-ui/src/lib/hooks/useDynamicDimensions';
import { allInsightsFocusAreaPersistedDimensionCacheKey } from 'apps/sigma2-ui/src/constants/usable-cache-keys';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

const InsightCardLoadingSkeleton: React.FC = () => {
  return <LoadingSkeleton heightClassName="h-64" widthClassName="w-96" />;
};

const InsightCardLoadingSkeletonFullWidth: React.FC = () => {
  return <LoadingSkeleton heightClassName="h-64" widthClassName="w-full" />;
};

const InsightsRow: React.FC<{
  insights: Insight[];
  isLoading: boolean;
  focusArea: MyFocusAreaDefinition;
  calibrationFraming: CalibrationFraming;
  dimensionsPayload: UseDimensionConfigPayload;
}> = ({
  insights,
  isLoading,
  focusArea,
  calibrationFraming,
  dimensionsPayload,
}) => {
  if (isLoading) {
    return (
      <Spacing direction="horizontal" size="gap-10" divider>
        <InsightCardLoadingSkeleton />
        <InsightCardLoadingSkeleton />
        <InsightCardLoadingSkeleton />
        <InsightCardLoadingSkeleton />
      </Spacing>
    );
  }

  if (!isLoading && insights.length > 0) {
    return (
      <Spacing direction="horizontal" size="gap-10" divider>
        {insights.map((insight) => (
          <InsightsCard
            key={getInsightKey(insight)}
            {...insight}
            focusArea={focusArea}
            calibrationFraming={calibrationFraming}
            insightImageStyle="hidden"
            dimensionsPayload={dimensionsPayload}
          />
        ))}
      </Spacing>
    );
  }

  return null;
};

const InsightsColumn: React.FC<{
  insights: Insight[];
  isLoading: boolean;
  focusArea: MyFocusAreaDefinition;
  calibrationFraming: CalibrationFraming;
  dimensionsPayload: UseDimensionConfigPayload;
}> = ({
  insights,
  isLoading,
  focusArea,
  calibrationFraming,
  dimensionsPayload,
}) => {
  if (isLoading) {
    return (
      <Spacing direction="vertical" size="gap-10" divider>
        <InsightCardLoadingSkeleton />
        <InsightCardLoadingSkeleton />
        <InsightCardLoadingSkeleton />
      </Spacing>
    );
  }

  if (!isLoading && insights.length > 0) {
    return (
      <Spacing direction="vertical" size="gap-10" divider>
        {insights.map((insight) => (
          <InsightsCard
            key={getInsightKey(insight)}
            {...insight}
            focusArea={focusArea}
            calibrationFraming={calibrationFraming}
            insightImageStyle="visible-top"
            dimensionsPayload={dimensionsPayload}
          />
        ))}
      </Spacing>
    );
  }

  return null;
};

const InsightsFullWidthRow: React.FC<{
  insights: [Insight];
  isLoading: boolean;
  focusArea: MyFocusAreaDefinition;
  calibrationFraming: CalibrationFraming;
  dimensionsPayload: UseDimensionConfigPayload;
}> = ({
  insights,
  isLoading,
  focusArea,
  calibrationFraming,
  dimensionsPayload,
}) => {
  const insight = insights[0];

  if (isLoading) {
    return <InsightCardLoadingSkeletonFullWidth />;
  }

  return (
    <InsightsCard
      {...insight}
      focusArea={focusArea}
      widthClassName="w-full"
      calibrationFraming={calibrationFraming}
      insightImageStyle="visible-right"
      dimensionsPayload={dimensionsPayload}
    />
  );
};

const InsightsColumns: React.FC<{
  insightRows: InsightGroupProps[];
  isLoading: boolean;
  focusArea: MyFocusAreaDefinition;
  calibrationFraming: CalibrationFraming;
  dimensionsPayload: UseDimensionConfigPayload;
}> = ({
  insightRows,
  isLoading,
  focusArea,
  calibrationFraming,
  dimensionsPayload,
}) => {
  if (!isLoading && insightRows.length === 0) {
    return null;
  }

  return (
    <Spacing direction="horizontal" size="gap-10" divider>
      {insightRows.map(({ insights }) => {
        return (
          <InsightsColumn
            key={getInsightKey(insights[0])}
            insights={insights}
            isLoading={isLoading}
            focusArea={focusArea}
            calibrationFraming={calibrationFraming}
            dimensionsPayload={dimensionsPayload}
          />
        );
      })}
    </Spacing>
  );
};

const InsightsRows: React.FC<{
  insightRows: InsightGroupProps[];
  isLoading: boolean;
  focusArea: MyFocusAreaDefinition;
  calibrationFraming: CalibrationFraming;
  dimensionsPayload: UseDimensionConfigPayload;
}> = ({
  insightRows,
  isLoading,
  focusArea,
  calibrationFraming,
  dimensionsPayload,
}) => {
  if (!isLoading && insightRows.length === 0) {
    return null;
  }

  return (
    <Spacing direction="vertical" size="gap-10" divider>
      {insightRows.map(({ insights }) => {
        return (
          <InsightsRow
            key={getInsightKey(insights[0])}
            insights={insights}
            isLoading={isLoading}
            focusArea={focusArea}
            calibrationFraming={calibrationFraming}
            dimensionsPayload={dimensionsPayload}
          />
        );
      })}
    </Spacing>
  );
};

const InsightsFullWidthRows: React.FC<{
  insightRows: InsightGroupProps[];
  isLoading: boolean;
  focusArea: MyFocusAreaDefinition;
  calibrationFraming: CalibrationFraming;
  dimensionsPayload: UseDimensionConfigPayload;
}> = ({
  insightRows,
  isLoading,
  focusArea,
  calibrationFraming,
  dimensionsPayload,
}) => {
  if (!isLoading && insightRows.length === 0) {
    return null;
  }

  return (
    <Spacing direction="vertical" size="gap-10" divider className="w-full">
      {insightRows.map(({ insights }) => {
        if (insights.length === 1) {
          return (
            <InsightsFullWidthRow
              key={getInsightKey(insights[0])}
              insights={insights as [Insight]}
              isLoading={isLoading}
              focusArea={focusArea}
              calibrationFraming={calibrationFraming}
              dimensionsPayload={dimensionsPayload}
            />
          );
        }

        return null;
      })}
    </Spacing>
  );
};

export const AllInsights: React.FC<{
  isLoading: boolean;
  insights: Insight[];
  focusArea: MyFocusAreaDefinition;
  observation: MyObservation;
  calibrationFraming: CalibrationFraming;
}> = ({ insights, focusArea, isLoading, calibrationFraming, observation }) => {
  const dimensionsConfigPayload = useDimensionConfig();
  const language = useLanguage();

  const dimensionsPayload = useDynamicDimensions(
    useMemo(() => {
      return {
        type: 'multi',
        focusArea: focusArea.focusArea,
        automaticallySelectAll: true,
        persistKey: allInsightsFocusAreaPersistedDimensionCacheKey({
          focusArea: focusArea.focusArea,
          webCacheKeyPrefix: observation.webCacheKeyPrefix,
        }),
      };
    }, [focusArea, observation]),
  );

  const [submittedDimensions, setSubmittedDimensions] = useState<string[]>([]);

  const filteredInsights = useMemo(() => {
    return insights.filter(
      (i) =>
        submittedDimensions.length === 0 ||
        submittedDimensions.includes(i.dimension),
    );
  }, [insights, submittedDimensions]);

  const insightsRows = useMemo(() => {
    return groupInsights(filteredInsights);
  }, [filteredInsights]);

  const [didAutoLoad, setDidAutoLoad] = useState(false);

  useEffect(() => {
    if (didAutoLoad) {
      return;
    }

    if (dimensionsPayload.selectedDimensions.length > 0) {
      setSubmittedDimensions(dimensionsPayload.selectedDimensions);
      setDidAutoLoad(true);
    }
  }, [didAutoLoad, dimensionsPayload]);

  const doAnalyze = useCallback(() => {
    setSubmittedDimensions(dimensionsPayload.selectedDimensions);
  }, [dimensionsPayload]);

  return (
    <Spacing direction="vertical" size="gap-10">
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />

      <S2Typography
        variant="headline-3"
        translatedContent={language.insightAllInsightsTitle()}
      />

      <FilterBar
        dynamicFiltersPayload={'none'}
        isProcessing={isLoading}
        areStaticFiltersEqual={() =>
          dimensionsPayload.selectedDimensions === submittedDimensions
        }
        staticFilters={
          <DimensionSelect {...dimensionsPayload} isProcessing={isLoading} />
        }
        slim={true}
        onAnalyze={() => {
          doAnalyze();
        }}
      />
      <Spacing direction="vertical" size="gap-10">
        <S2Typography
          variant="body-7"
          translatedContent={language.insightAllInsightsSubtitle()}
        />

        {insightsRows.length === 0 && (
          <S2Typography
            variant="body-7"
            translatedContent={language.insightsNoInsightsFound()}
          />
        )}

        <Spacing direction="vertical" size="gap-10" divider>
          <InsightsColumns
            isLoading={isLoading}
            insightRows={insightsRows.filter((x) => x.type === 'column')}
            focusArea={focusArea}
            calibrationFraming={calibrationFraming}
            dimensionsPayload={dimensionsConfigPayload}
          />

          <InsightsRows
            isLoading={isLoading}
            insightRows={insightsRows.filter((x) => x.type === 'row')}
            focusArea={focusArea}
            calibrationFraming={calibrationFraming}
            dimensionsPayload={dimensionsConfigPayload}
          />

          <InsightsFullWidthRows
            isLoading={isLoading}
            insightRows={insightsRows.filter((x) => x.type === 'fullWidthRow')}
            focusArea={focusArea}
            calibrationFraming={calibrationFraming}
            dimensionsPayload={dimensionsConfigPayload}
          />
        </Spacing>
      </Spacing>
    </Spacing>
  );
};
